import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { useNotificationContext } from '../context/NotificationContext';
import {
  Bell,
  CheckCircle,
  XCircle,
  Clock,
  Award,
  FileText,
  Play,
  Loader2,
  Check,
  Trash2,
  MessageSquare,
  AtSign,
  Mail,
  Megaphone,
  AlertTriangle,
  Wifi,
  WifiOff,
  ChevronRight,
  Sparkles
} from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';

const NotificationsDropdown = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  // Use shared notification context (singleton WebSocket)
  const {
    notifications,
    unreadCount,
    loading,
    connected,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    fetchNotifications,
    // 2026-02-24 — Missed Reviews tray.
    missedActions,
    fetchMissedActions,
    restoreMissedAction,
    // 2026-02-24 — Manual pending-review queue trigger. Lets the user
    // pull the ActionDialogQueue modal up on demand from the bell,
    // even when they're inside a lab route (where the queue would
    // otherwise auto-suppress). See useIsInFocusedFlow.js.
    pendingActions,
    openPendingActionsQueue,
  } = useNotificationContext();

  // 2026-02-24 — Missed Reviews accordion. Collapsed by default; opens
  // on click and lazy-fetches the list.
  const [missedOpen, setMissedOpen] = useState(false);
  const missedCount = missedActions?.length || 0;
  const pendingCount = pendingActions?.length || 0;

  // Fetch notifications when dropdown opens
  useEffect(() => {
    if (open) {
      fetchNotifications({ limit: 50 });
      // Also fetch missed reviews so the accordion count is accurate
      // the moment the user opens the bell.
      fetchMissedActions();
    }
  }, [open, fetchNotifications, fetchMissedActions]);

  // Get only the latest 20 notifications for the dropdown
  const recentNotifications = notifications.slice(0, 20);

  const handleNotificationClick = (notification) => {
    if (!notification.isRead) {
      markAsRead(notification.id);
    }
    if (notification.link) {
      navigate(notification.link, { state: { from: '/notifications' } });
      setOpen(false);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'lab_execution_started':
        return <Play className="w-4 h-4 text-blue-500" />;
      case 'lab_execution_completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'lab_execution_failed':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'lab_submission_received':
        return <FileText className="w-4 h-4 text-purple-500" />;
      case 'lab_graded':
      case 'quiz_graded':
      case 'assignment_graded':
        return <Award className="w-4 h-4 text-amber-500" />;
      case 'new_post':
      case 'new_comment':
      case 'discussion_reply':
        return <MessageSquare className="w-4 h-4 text-blue-500" />;
      case 'mention':
      case 'discussion_mention':
        return <AtSign className="w-4 h-4 text-cyan-500" />;
      case 'dm_received':
        return <Mail className="w-4 h-4 text-pink-500" />;
      case 'announcement':
      case 'system_announcement':
        return <Megaphone className="w-4 h-4 text-orange-500" />;
      case 'at_risk_alert':
        return <AlertTriangle className="w-4 h-4 text-red-500" />;
      case 'deadline_approaching':
      case 'deadline_overdue':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      default:
        return <Bell className="w-4 h-4 text-gray-500" />;
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="relative">
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <span
              data-testid="notification-bell-badge"
              className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center animate-pulse"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-sm">Notifications</h3>
            {/* Connection indicator */}
            {connected ? (
              <Wifi className="w-3 h-3 text-green-500" title="Connected - Real-time updates" />
            ) : (
              <WifiOff className="w-3 h-3 text-gray-400" title="Reconnecting..." />
            )}
          </div>
          {unreadCount > 0 && (
            <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => markAllAsRead()}>
              <Check className="w-3 h-3 mr-1" />
              Mark all read
            </Button>
          )}
        </div>
        
        {/* Notifications List */}
        <ScrollArea className="h-80">
          {/* 2026-02-24 — Pending Reviews trigger. Lets the user pull
              up the ActionDialogQueue modal on demand from the bell —
              needed because the queue auto-suppresses on lab/content
              routes. Only shown when there are actual pending items. */}
          {pendingCount > 0 && (
            <div className="border-b bg-violet-50/60" data-testid="pending-reviews-trigger-row">
              <button
                type="button"
                onClick={() => {
                  openPendingActionsQueue();
                  setOpen(false);  // close the dropdown so the dialog can pop cleanly
                }}
                className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-violet-100/60 transition-colors"
                data-testid="pending-reviews-trigger-btn"
              >
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-violet-600" />
                  <span className="text-xs font-semibold text-violet-900">
                    Pending reviews
                  </span>
                  <Badge variant="outline" className="text-[10px] h-4 border-violet-300 text-violet-800 bg-white">
                    {pendingCount}
                  </Badge>
                </div>
                <span className="text-[11px] text-violet-700 font-medium">
                  Review now →
                </span>
              </button>
            </div>
          )}

          {/* 2026-02-24 — Missed Reviews accordion. Auto-expired
              action_required notifs (>14d without action). Kept
              forever so users don't silently lose Stage-2 reviews
              they missed while on holiday. */}
          {missedCount > 0 && (
            <div className="border-b bg-amber-50/60" data-testid="missed-reviews-section">
              <button
                type="button"
                onClick={() => setMissedOpen((v) => !v)}
                className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-amber-100/60 transition-colors"
                data-testid="missed-reviews-toggle"
              >
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-amber-600" />
                  <span className="text-xs font-semibold text-amber-900">
                    Missed reviews
                  </span>
                  <Badge variant="outline" className="text-[10px] h-4 border-amber-300 text-amber-800 bg-white">
                    {missedCount}
                  </Badge>
                </div>
                <ChevronRight
                  className={`w-3 h-3 text-amber-700 transition-transform ${missedOpen ? 'rotate-90' : ''}`}
                />
              </button>
              {missedOpen && (
                <div className="divide-y divide-amber-100" data-testid="missed-reviews-list">
                  {missedActions.map((n) => (
                    <div key={n.id} className="p-3 bg-white/60">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-slate-900 line-clamp-1">
                            {n.title}
                          </p>
                          <p className="text-[11px] text-slate-600 mt-0.5 line-clamp-2">
                            {n.message}
                          </p>
                          <p className="text-[10px] text-amber-700 mt-1">
                            Expired · {formatTime(n?.metadata?.missedAt || n.createdAt)}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-6 text-[10px] border-amber-300 text-amber-800 hover:bg-amber-100 whitespace-nowrap"
                          onClick={() => restoreMissedAction(n.id)}
                          data-testid={`missed-restore-${n.id}`}
                        >
                          Handle now
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : recentNotifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 text-gray-500">
              <Bell className="w-8 h-8 mb-2 text-gray-300" />
              <p className="text-sm">No notifications</p>
              <p className="text-xs text-gray-400 mt-1">You&apos;re all caught up!</p>
            </div>
          ) : (
            <div className="divide-y">
              {recentNotifications.map((notification) => (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`p-3 cursor-pointer hover:bg-gray-50 transition-colors ${
                    !notification.isRead ? 'bg-blue-50/50' : ''
                  }`}
                >
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm ${!notification.isRead ? 'font-medium' : ''}`}>
                          {notification.title}
                        </p>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteNotification(notification.id);
                          }}
                          className="opacity-0 hover:opacity-100 transition-opacity p-1 hover:bg-gray-200 rounded"
                        >
                          <Trash2 className="w-3 h-3 text-gray-400" />
                        </button>
                      </div>
                      <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">
                        {notification.message}
                      </p>
                      <p className="text-[10px] text-gray-400 mt-1">
                        {formatTime(notification.createdAt)}
                      </p>
                    </div>
                    {!notification.isRead && (
                      <div className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0 mt-1.5" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
        
        {/* Footer - View All */}
        <div className="p-2 border-t">
          <Button 
            variant="ghost" 
            size="sm" 
            className="w-full text-xs justify-between"
            onClick={() => {
              navigate('/notifications');
              setOpen(false);
            }}
          >
            View all notifications
            <ChevronRight className="w-3 h-3" />
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationsDropdown;
